import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  HStack,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { formatDistanceToNow } from "date-fns";
import { ja } from "date-fns/locale";
import React, { FC, useCallback, useState } from "react";

import type { VersionModuleSnapshotRow } from "./buildLinkedModuleDiffsFromSnapshots";
import type { WorkspacePendingSummary } from "./moduleWorkspacePendingSummary";
import { PendingChangeSummaryLists } from "./PendingChangeSummaryLists";

export type CompiledSnapshot = {
  id: string;
  versionName: string;
  description?: string;
  authorName?: string;
  isActive: boolean;
  createdAt: string;
  content?: string;
  /** null on legacy versions without change_summary */
  changeSummary: WorkspacePendingSummary | null;
  /** null on legacy versions without modules_snapshot */
  modulesSnapshot: readonly VersionModuleSnapshotRow[] | null;
};

type Props = {
  snapshots: CompiledSnapshot[];
  onActivateSnapshot?: (snapshotId: string) => Promise<void>;
  onShowDiff?: (
    snapshotA: CompiledSnapshot,
    snapshotB: CompiledSnapshot
  ) => void;
  isLoading?: boolean;
};

export const SnapshotHistory: FC<Props> = ({
  snapshots,
  onActivateSnapshot,
  onShowDiff,
  isLoading = false,
}) => {
  const [rollbackSnapshot, setRollbackSnapshot] =
    useState<CompiledSnapshot | null>(null);

  const {
    isOpen: isRollbackOpen,
    onOpen: onRollbackOpen,
    onClose: onRollbackClose,
  } = useDisclosure();

  const cancelRef = React.useRef<HTMLButtonElement>(null);

  const handleRollback = useCallback(
    (snapshot: CompiledSnapshot) => {
      setRollbackSnapshot(snapshot);
      onRollbackOpen();
    },
    [onRollbackOpen]
  );

  const handleRollbackConfirm = useCallback(async () => {
    if (rollbackSnapshot && onActivateSnapshot) {
      try {
        await onActivateSnapshot(rollbackSnapshot.id);
        onRollbackClose();
      } catch (error) {
        // Error handling is done by parent
      }
    }
  }, [rollbackSnapshot, onActivateSnapshot, onRollbackClose]);

  const handleDiff = useCallback(
    (snapshot: CompiledSnapshot) => {
      const currentSnapshot = snapshots.find((s) => s.isActive);
      if (currentSnapshot && onShowDiff) {
        onShowDiff(snapshot, currentSnapshot);
      }
    },
    [snapshots, onShowDiff]
  );

  const handleDiffClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      const snapshotId = event.currentTarget.dataset.snapshotId;
      const snapshot = snapshots.find((s) => s.id === snapshotId);
      if (snapshot) {
        handleDiff(snapshot);
      }
    },
    [handleDiff, snapshots]
  );

  const handleRollbackClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      const snapshotId = event.currentTarget.dataset.snapshotId;
      const snapshot = snapshots.find((s) => s.id === snapshotId);
      if (snapshot) {
        handleRollback(snapshot);
      }
    },
    [handleRollback, snapshots]
  );

  const stopAccordionToggle = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.stopPropagation();
    },
    []
  );

  if (snapshots.length === 0) {
    return (
      <Box textAlign="center" py={8}>
        <Text color="gray.500">スナップショットがありません</Text>
        <Text fontSize="sm" color="gray.400" mt={2}>
          モジュール一覧のコンパイル済みプレビューから「変更を公開」で公開するとスナップショットが作成されます
        </Text>
      </Box>
    );
  }

  return (
    <>
      <Accordion allowMultiple>
        {snapshots.map((snapshot) => (
          <AccordionItem
            key={snapshot.id}
            border="1px solid"
            borderColor={snapshot.isActive ? "blue.200" : "gray.200"}
            borderRadius="md"
            mb={3}
            bg={snapshot.isActive ? "blue.50" : "white"}
            overflow="hidden"
            _last={{ mb: 0 }}
          >
            <AccordionButton
              px={4}
              py={4}
              _hover={{ bg: snapshot.isActive ? "blue.100" : "gray.50" }}
              _focus={{ boxShadow: "none" }}
            >
              <VStack
                align="start"
                spacing={1}
                flex={1}
                minW={0}
                textAlign="left"
              >
                <HStack>
                  <Text
                    fontWeight="semibold"
                    color={snapshot.isActive ? "blue.700" : "gray.800"}
                  >
                    {snapshot.versionName}
                  </Text>
                  {snapshot.isActive && <Badge colorScheme="blue">現在</Badge>}
                </HStack>

                {snapshot.description && (
                  <Text fontSize="sm" color="gray.600" noOfLines={2}>
                    {snapshot.description}
                  </Text>
                )}

                <HStack spacing={4}>
                  <Text fontSize="xs" color="gray.500">
                    {formatDistanceToNow(new Date(snapshot.createdAt), {
                      addSuffix: true,
                      locale: ja,
                    })}
                  </Text>
                  {snapshot.authorName && (
                    <Text fontSize="xs" color="gray.500">
                      作成者: {snapshot.authorName}
                    </Text>
                  )}
                </HStack>
              </VStack>

              {!snapshot.isActive && (
                <HStack
                  spacing={2}
                  flexShrink={0}
                  ml={3}
                  onClick={stopAccordionToggle}
                >
                  <Button
                    size="xs"
                    colorScheme="blue"
                    onClick={handleDiffClick}
                    data-snapshot-id={snapshot.id}
                    isDisabled={!onShowDiff}
                  >
                    差分
                  </Button>

                  <Button
                    size="xs"
                    colorScheme="orange"
                    onClick={handleRollbackClick}
                    data-snapshot-id={snapshot.id}
                    isDisabled={!onActivateSnapshot}
                    isLoading={isLoading}
                  >
                    ロールバック
                  </Button>
                </HStack>
              )}

              <AccordionIcon flexShrink={0} ml={2} />
            </AccordionButton>

            <AccordionPanel
              px={4}
              pb={4}
              pt={0}
              borderTopWidth="1px"
              borderColor={snapshot.isActive ? "blue.100" : "gray.100"}
            >
              <Box pt={3}>
                <Text
                  fontSize="sm"
                  fontWeight="semibold"
                  mb={2}
                  color="gray.700"
                >
                  変更サマリー
                </Text>
                {snapshot.changeSummary == null ? (
                  <Text fontSize="sm" color="gray.500">
                    サマリーなし
                  </Text>
                ) : (
                  <PendingChangeSummaryLists summary={snapshot.changeSummary} />
                )}
              </Box>
            </AccordionPanel>
          </AccordionItem>
        ))}
      </Accordion>

      {/* Rollback Confirmation Dialog */}
      {isRollbackOpen && (
        <AlertDialog
          isOpen={isRollbackOpen}
          leastDestructiveRef={cancelRef}
          onClose={onRollbackClose}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader>
                スナップショットをロールバック
              </AlertDialogHeader>

              <AlertDialogBody>
                「{rollbackSnapshot?.versionName}」にロールバックしますか？
                現在のモジュール設定が変更され、本番環境に即座に反映されます。
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={onRollbackClose}>
                  キャンセル
                </Button>
                <Button
                  colorScheme="orange"
                  onClick={handleRollbackConfirm}
                  ml={3}
                  isLoading={isLoading}
                >
                  ロールバック
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      )}
    </>
  );
};
